import { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import DocumentViewer from '@/components/DocumentViewer';
import toast from 'react-hot-toast';
import { useRouter } from 'next/router';
import { Documento, DatosFacturaIA } from '@/types';
import { createIncidencia, addMensajeIncidencia } from '@/lib/supabaseService';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

/* ─── PDF Viewer (client-only) ─── */
const PdfViewer = dynamic(() => import('@/components/PdfViewer'), {
  ssr: false,
  loading: () => (
    <div className="h-full flex items-center justify-center bg-slate-100">
      <div className="text-center space-y-3">
        <span className="material-symbols-outlined text-[48px] text-calm-accent animate-pulse">picture_as_pdf</span>
        <p className="text-sm font-light text-calm-muted">Cargando visor PDF…</p>
      </div>
    </div>
  ),
});

/* ─── Icon helper ─── */
const Icon = ({ name, className }: { name: string; className?: string }) => (
  <span className={cn('material-symbols-outlined', className)}>{name}</span>
);

/* ─── Status configuration ─── */

const ESTADO_INFO: Record<string, { label: string; dot: string; badge: string }> = {
  pendiente:      { label: 'Pendiente',      dot: 'bg-amber-400',   badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  contabilizado:  { label: 'Contabilizado',  dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  error:          { label: 'Error',          dot: 'bg-red-500',     badge: 'bg-red-50 text-red-700 border-red-200' },
  en_proceso:     { label: 'En proceso',     dot: 'bg-blue-500',    badge: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const STATUS_PILLS: Array<{ key: string; label: string }> = [
  { key: 'todos',          label: 'Todos' },
  { key: 'pendiente',      label: 'Pendientes' },
  { key: 'contabilizado',  label: 'Contabilizados' },
  { key: 'error',          label: 'Con errores' },
];

const getEstadoInfo = (estado: string) =>
  ESTADO_INFO[estado] || { label: estado || 'Desconocido', dot: 'bg-slate-300', badge: 'bg-slate-50 text-calm-muted border-calm-border' };

/* ─── Interfaces ─── */

interface Cliente {
  id: string;
  razon_social: string;
  nombre_comercial: string;
  nif: string;
}

interface DocumentoDB {
  id: string;
  nombre: string;
  cliente_id: string;
  tipo: string;
  estado: string;
  fecha_subida: string;
  procesado_ia: boolean;
  tamanio: number;
  url_archivo: string;
  datos_ia?: DatosFacturaIA & { notas?: string };
  nro_asiento_erp?: string;
}

interface ClienteStats {
  cliente: Cliente;
  totalDocumentos: number;
  pendientes: number;
  procesados: number;
  errores: number;
  ultimoDocumento?: Date;
}

export default function AsesorContabilizacionManual() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [documentos, setDocumentos] = useState<DocumentoDB[]>([]);
  const [clientes, setClientes] = useState<Map<string, Cliente>>(new Map());
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState<DocumentoDB | null>(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<string | null>(null);
  const [vistaActual, setVistaActual] = useState<'clientes' | 'documentos'>('clientes');
  const [documentosPendientes, setDocumentosPendientes] = useState<DocumentoDB[]>([]);
  const [indiceDocumentoActual, setIndiceDocumentoActual] = useState<number>(0);
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [ultimoNumeroAsiento, setUltimoNumeroAsiento] = useState<string>('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /* New state for upgrades */
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [formSections, setFormSections] = useState({ datos: true, asiento: true, notas: true });

  // Convert database format to Documento interface format
  const mapDocumentoToInterface = (doc: DocumentoDB): Documento => {
    return {
      id: doc.id,
      nombre: doc.nombre,
      clienteId: doc.cliente_id,
      asesorId: null,
      tipo: doc.tipo as Documento['tipo'],
      estado: doc.estado as Documento['estado'],
      fechaSubida: new Date(doc.fecha_subida),
      procesadoIA: doc.procesado_ia,
      tamaño: doc.tamanio,
      urlArchivo: doc.url_archivo,
      nroAsientoERP: doc.nro_asiento_erp,
      datosIA: doc.datos_ia,
    };
  };
  
  const [formularioAsiento, setFormularioAsiento] = useState({
    tipoDocumento: '',
    asiento: '',
    notas: '',
  });

  const loadUnprocessedDocuments = useCallback(async () => {
    try {
      setLoadingData(true);
      
      if (!user?.id) {
        console.error('No user ID found');
        toast.error('Error: No se pudo obtener el ID del usuario');
        return;
      }

      console.log('Loading documents for advisor:', user.id);
      
      // Get advisor's assigned clients
      const { data: assignedClients, error: clientError } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(*)')
        .eq('asesor_id', user.id);

      if (clientError) {
        console.error('Error loading assigned clients:', clientError);
        throw clientError;
      }

      console.log('Assigned clients:', assignedClients);

      const clientIds = assignedClients?.map(ac => ac.cliente_id) || [];

      if (clientIds.length === 0) {
        console.log('No assigned clients found');
        setDocumentos([]);
        return;
      }

      // Build clients map
      const clientsMap = new Map<string, Cliente>();
      assignedClients?.forEach(ac => {
        if (ac.clientes) {
          clientsMap.set(ac.cliente_id, ac.clientes as unknown as Cliente);
        }
      });
      setClientes(clientsMap);

      // Get all documents for manual accounting (expanded states for filter pills)
      const { data: allDocs, error: docsError } = await supabase
        .from('documentos')
        .select('*')
        .in('cliente_id', clientIds)
        .in('estado', ['pendiente', 'contabilizado', 'error', 'en_proceso']);

      if (docsError) {
        console.error('Error loading documents:', docsError);
        throw docsError;
      }

      console.log('All documents:', allDocs);
      setDocumentos(allDocs || []);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error loading documents:', error);
      toast.error(`Error al cargar documentos: ${msg}`);
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user && user.rol === 'asesor') {
      loadUnprocessedDocuments();
    }
  }, [user, loading, router, loadUnprocessedDocuments]);

  // Handle cliente query parameter
  useEffect(() => {
    if (router.query.cliente && typeof router.query.cliente === 'string' && !loadingData) {
      const clienteId = router.query.cliente;
      // Wait for clients to be loaded
      if (clientes.size > 0 && clientes.has(clienteId)) {
        setClienteSeleccionado(clienteId);
        const docsCliente = getDocumentosDelCliente(clienteId).filter(d => d.estado === 'pendiente');
        setDocumentosPendientes(docsCliente);
        setIndiceDocumentoActual(0);
        setSeleccionados(new Set());
        setVistaActual('documentos');
      }
    }
  }, [router.query.cliente, clientes, loadingData]);

  const handleVerDocumento = (doc: DocumentoDB, index?: number) => {
    setDocumentoSeleccionado(doc);
    if (index !== undefined) {
      setIndiceDocumentoActual(index);
    }
    setMostrarFormulario(true);
    // Reset form with AI data if available
    if (doc.datos_ia) {
      setFormularioAsiento({
        tipoDocumento: doc.datos_ia.tipoDocumento || '',
        asiento: doc.nro_asiento_erp || '',
        notas: doc.datos_ia.notas || '',
      });
    } else {
      setFormularioAsiento({
        tipoDocumento: '',
        asiento: '',
        notas: '',
      });
    }
  };

  const calcularSiguienteNumeroAsiento = (): string => {
    if (!formularioAsiento.asiento) return '';
    
    // Try to extract a number from the current asiento value
    const match = formularioAsiento.asiento.match(/(\d+)$/);
    if (match) {
      const numero = parseInt(match[1], 10);
      const siguienteNumero = numero + 1;
      // Replace the last number with the next one
      return formularioAsiento.asiento.replace(/\d+$/, siguienteNumero.toString());
    }
    
    // If it's just a number, increment it
    const asNumero = parseInt(formularioAsiento.asiento, 10);
    if (!isNaN(asNumero)) {
      return (asNumero + 1).toString();
    }
    
    return formularioAsiento.asiento;
  };

  const handleSiguienteDocumento = () => {
    // Save the current asiento number for increment
    const siguienteNumero = calcularSiguienteNumeroAsiento();
    
    if (indiceDocumentoActual < documentosPendientes.length - 1) {
      const siguiente = documentosPendientes[indiceDocumentoActual + 1];
      setIndiceDocumentoActual(indiceDocumentoActual + 1);
      setDocumentoSeleccionado(siguiente);
      
      // Pre-fill the form with incremented asiento number
      setFormularioAsiento({
        tipoDocumento: formularioAsiento.tipoDocumento, // Keep same type
        asiento: siguienteNumero,
        notas: '',
      });
    } else {
      // No more documents
      toast.success('No hay más documentos pendientes');
      setMostrarFormulario(false);
      setDocumentoSeleccionado(null);
    }
  };

  const handleTerminar = () => {
    setMostrarFormulario(false);
    setDocumentoSeleccionado(null);
    // Stay in documents list view
  };

  const handleCrearIncidencia = async () => {
    try {
      if (!documentoSeleccionado) return;

      // Validate that notes are provided
      if (!formularioAsiento.notas || !formularioAsiento.notas.trim()) {
        toast.error('Por favor explica el motivo de la incidencia en el campo Notas');
        return;
      }

      if (!user?.id) {
        toast.error('No se pudo obtener el ID del usuario');
        return;
      }

      // Create the incidencia
      const incidenciaId = await createIncidencia({
        documentoId: documentoSeleccionado.id,
        clienteId: documentoSeleccionado.cliente_id,
        asesorId: user.id,
        estado: 'nueva',
        origen: 'asesor',
      });

      // Add the first message with the notes
      await addMensajeIncidencia(incidenciaId, {
        usuarioId: user.id,
        rol: 'asesor',
        mensaje: formularioAsiento.notas.trim(),
        esIA: false,
      });

      // Update document status to 'incidencia'
      const response = await fetch('/api/documents/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentoId: documentoSeleccionado.id,
          updates: {
            estado: 'incidencia',
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al actualizar el estado del documento');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Error al actualizar el estado del documento');
      }

      toast.success('Incidencia creada correctamente. El documento ha sido marcado como incidencia.');

      // Remove from pending list
      const nuevaLista = documentosPendientes.filter(d => d.id !== documentoSeleccionado.id);
      setDocumentosPendientes(nuevaLista);

      // Reload documents
      await loadUnprocessedDocuments();

      // Auto-navigate to next document or close
      if (nuevaLista.length > 0) {
        const siguienteDoc = indiceDocumentoActual < nuevaLista.length 
          ? nuevaLista[indiceDocumentoActual] 
          : nuevaLista[nuevaLista.length - 1];
        
        setDocumentoSeleccionado(siguienteDoc);
        
        // Reset form
        setFormularioAsiento({
          tipoDocumento: '',
          asiento: '',
          notas: '',
        });
      } else {
        toast('No hay más documentos pendientes');
        handleVolverAClientes();
        
        // Reset form
        setFormularioAsiento({
          tipoDocumento: '',
          asiento: '',
          notas: '',
        });
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Error desconocido al crear la incidencia';
      console.error('Error creating incidencia:', error);
      toast.error(`Error: ${msg}`);
    }
  };

  const handleEliminarDocumentoActual = async () => {
    if (!documentoSeleccionado) return;
    
    if (!confirm('¿Estás seguro de que deseas eliminar este documento?')) {
      return;
    }

    try {
      const response = await fetch('/api/documents/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentoId: documentoSeleccionado.id }),
      });

      if (!response.ok) {
        throw new Error('Error al eliminar el documento');
      }

      toast.success('Documento eliminado');
      
      // Remove from pending list
      const nuevaLista = documentosPendientes.filter(d => d.id !== documentoSeleccionado.id);
      setDocumentosPendientes(nuevaLista);
      
      // Reload all documents
      await loadUnprocessedDocuments();
      
      // Move to next document or close
      if (nuevaLista.length > 0) {
        if (indiceDocumentoActual < nuevaLista.length) {
          handleVerDocumento(nuevaLista[indiceDocumentoActual], indiceDocumentoActual);
        } else {
          handleVerDocumento(nuevaLista[nuevaLista.length - 1], nuevaLista.length - 1);
        }
      } else {
        toast('No hay más documentos pendientes');
        handleVolverAClientes();
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Error al eliminar';
      toast.error(`Error al eliminar: ${msg}`);
    }
  };


  const handleGuardarAsiento = async () => {
    try {
      if (!documentoSeleccionado) return;

      // Validate required fields
      if (!formularioAsiento.tipoDocumento) {
        toast.error('Por favor selecciona el tipo de documento');
        return;
      }
      if (!formularioAsiento.asiento) {
        toast.error('Por favor ingresa el número de asiento');
        return;
      }

      // Build datosIA object from form data for archiving
      const existingDatosIA = documentoSeleccionado.datos_ia || {};
      const datosIA = {
        ...existingDatosIA,
        tipoDocumento: formularioAsiento.tipoDocumento,
        notas: formularioAsiento.notas,
        nivelConfianza: 100, // Manual entry has 100% confidence
      };

      // Update document status via API endpoint
      let response: Response;
      try {
        response = await fetch('/api/documents/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentoId: documentoSeleccionado.id,
            updates: {
              estado: 'contabilizado',
              procesadoIA: true,
              nroAsientoERP: formularioAsiento.asiento,
              datosIA: datosIA,
            },
          }),
        });
      } catch (fetchError: unknown) {
        console.error('Network error:', fetchError);
        throw new Error(
          'Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.'
        );
      }

      if (!response.ok) {
        let errorMessage = 'Error al actualizar el documento';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Error al actualizar el documento');
      }

      // Create incidencia if notes are provided
      if (formularioAsiento.notas && formularioAsiento.notas.trim()) {
        try {
          if (!user?.id) {
            throw new Error('No se pudo obtener el ID del usuario');
          }

          // Create the incidencia
          const incidenciaId = await createIncidencia({
            documentoId: documentoSeleccionado.id,
            clienteId: documentoSeleccionado.cliente_id,
            asesorId: user.id,
            estado: 'nueva',
            origen: 'asesor',
          });

          // Add the first message with the notes
          await addMensajeIncidencia(incidenciaId, {
            usuarioId: user.id,
            rol: 'asesor',
            mensaje: formularioAsiento.notas.trim(),
            esIA: false,
          });

          toast.success('Documento contabilizado y incidencia creada');
        } catch (incidenciaError: unknown) {
          const incMsg = incidenciaError instanceof Error ? incidenciaError.message : 'Error desconocido';
          console.error('Error creating incidencia:', incidenciaError);
          // Don't fail the whole operation if incidencia creation fails
          toast.error(`Documento contabilizado, pero error al crear incidencia: ${incMsg}`);
        }
      } else {
        toast.success('Documento contabilizado y enviado a ERP Cegid Diez');
      }
      
      // Calculate next asiento number
      const siguienteNumero = calcularSiguienteNumeroAsiento();
      
      // Remove from pending list
      const nuevaLista = documentosPendientes.filter(d => d.id !== documentoSeleccionado.id);
      setDocumentosPendientes(nuevaLista);
      
      // Reload documents
      await loadUnprocessedDocuments();
      
      // Auto-navigate to next document
      if (nuevaLista.length > 0) {
        const siguienteDoc = indiceDocumentoActual < nuevaLista.length 
          ? nuevaLista[indiceDocumentoActual] 
          : nuevaLista[nuevaLista.length - 1];
        
        setDocumentoSeleccionado(siguienteDoc);
        
        // Pre-fill form with incremented asiento
        setFormularioAsiento({
          tipoDocumento: formularioAsiento.tipoDocumento, // Keep same type
          asiento: siguienteNumero,
          notas: '',
        });
      } else {
        toast('No hay más documentos pendientes');
        handleVolverAClientes();
        
        // Reset form
        setFormularioAsiento({
          tipoDocumento: '',
          asiento: '',
          notas: '',
        });
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Error desconocido al guardar el asiento contable';
      console.error('Error saving entry:', error);
      toast.error(`Error: ${msg}`);
    }
  };

  const handleEliminarSeleccionados = async () => {
    if (seleccionados.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }

    if (!confirm(`¿Estás seguro de que deseas eliminar ${seleccionados.size} documento(s)?`)) {
      return;
    }

    setBulkActionLoading(true);
    toast.loading('Eliminando...');
    try {
      const ids = Array.from(seleccionados);
      let ok = 0;
      let err = 0;
      for (const docId of ids) {
        try {
          const res = await fetch('/api/documents/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ documentoId: docId }),
          });
          if (res.ok) ok++;
          else throw new Error();
        } catch {
          err++;
        }
      }
      toast.dismiss();
      if (err) toast.error(`Eliminados: ${ok}. Fallos: ${err}`);
      else toast.success(`${ok} documento(s) eliminado(s)`);
      setSeleccionados(new Set());
      await loadUnprocessedDocuments();
      if (clienteSeleccionado) {
        const docsCliente = getDocumentosDelCliente(clienteSeleccionado).filter(d => d.estado === 'pendiente');
        setDocumentosPendientes(docsCliente);
      }
    } catch (error: unknown) {
      toast.dismiss();
      toast.error('Error al eliminar documentos');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDescargarZip = async () => {
    if (seleccionados.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }

    const ids = Array.from(seleccionados);
    setBulkActionLoading(true);
    toast.loading('Preparando ZIP...');
    try {
      const res = await fetch('/api/documents/download-zip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentoIds: ids }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Error al descargar ZIP');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documentos-contabilizacion-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('ZIP descargado');
    } catch (error: unknown) {
      toast.dismiss();
      const msg = error instanceof Error ? error.message : 'Error al descargar ZIP';
      toast.error(msg);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const toggleSeleccion = (docId: string) => {
    const newSeleccionados = new Set(seleccionados);
    if (newSeleccionados.has(docId)) {
      newSeleccionados.delete(docId);
    } else {
      newSeleccionados.add(docId);
    }
    setSeleccionados(newSeleccionados);
  };

  const handleContabilizarDocumento = async (doc: DocumentoDB, index: number) => {
    handleVerDocumento(doc, index);
  };

  const handleEliminarDocumento = async (docId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este documento?')) {
      return;
    }

    try {
      const response = await fetch('/api/documents/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentoId: docId }),
      });

      if (!response.ok) {
        throw new Error('Error al eliminar el documento');
      }

      toast.success('Documento eliminado');
      await loadUnprocessedDocuments();
      
      // Update pending documents list
      if (clienteSeleccionado) {
        const docsCliente = getDocumentosDelCliente(clienteSeleccionado).filter(d => d.estado === 'pendiente');
        setDocumentosPendientes(docsCliente);
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Error al eliminar';
      toast.error(`Error al eliminar: ${msg}`);
    }
  };

  const getClienteById = (id: string): Cliente | undefined => {
    return clientes.get(id);
  };

  // Calculate document stats per client
  const getClienteStats = (): ClienteStats[] => {
    const statsMap = new Map<string, ClienteStats>();

    // Initialize stats for all clients
    clientes.forEach((cliente, clienteId) => {
      statsMap.set(clienteId, {
        cliente,
        totalDocumentos: 0,
        pendientes: 0,
        procesados: 0,
        errores: 0,
      });
    });

    // Count documents by client
    documentos.forEach((doc) => {
      const stats = statsMap.get(doc.cliente_id);
      if (stats) {
        stats.totalDocumentos++;
        if (doc.estado === 'contabilizado') {
          stats.procesados++;
        } else if (doc.estado === 'error') {
          stats.errores++;
        } else {
          stats.pendientes++;
        }
        const docDate = new Date(doc.fecha_subida);
        if (!stats.ultimoDocumento || docDate > stats.ultimoDocumento) {
          stats.ultimoDocumento = docDate;
        }
      }
    });

    // Convert to array and show all clients (even those without documents)
    return Array.from(statsMap.values())
      .sort((a, b) => {
        // Sort by pending documents first (descending), then by total documents
        if (b.pendientes !== a.pendientes) {
          return b.pendientes - a.pendientes;
        }
        return b.totalDocumentos - a.totalDocumentos;
      });
  };

  const handleClienteClick = (clienteId: string) => {
    setClienteSeleccionado(clienteId);
    const docsCliente = getDocumentosDelCliente(clienteId).filter(d => d.estado === 'pendiente');
    setDocumentosPendientes(docsCliente);
    setIndiceDocumentoActual(0);
    setSeleccionados(new Set());
    setStatusFilter('todos');
    setVistaActual('documentos');
    // Update URL without reloading
    router.push(`/asesor/contabilizacion-manual?cliente=${clienteId}`, undefined, { shallow: true });
  };

  const handleVolverAClientes = () => {
    setClienteSeleccionado(null);
    setVistaActual('clientes');
    setMostrarFormulario(false);
    setDocumentoSeleccionado(null);
    setDocumentosPendientes([]);
    setIndiceDocumentoActual(0);
    setSeleccionados(new Set());
    setStatusFilter('todos');
  };

  // Helper function to get document type display info
  const getTipoDocumentoInfo = (tipo: string) => {
    if (tipo === 'emitida_pdf') return 'Facturas emitidas (PDF)';
    if (tipo === 'recibida_pdf') return 'Factura recibidas (PDF)';
    if (tipo === 'emitida_excel') return 'Facturas emitidas (Excel)';
    if (tipo === 'recibida_excel') return 'Facturas recibidas (Excel)';
    return 'Otros';
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedDocumentos = (docs: DocumentoDB[]): DocumentoDB[] => {
    if (!sortColumn) return docs;

    return [...docs].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortColumn) {
        case 'documento':
          aValue = a.nombre.toLowerCase();
          bValue = b.nombre.toLowerCase();
          break;
        case 'empresa':
          aValue = getClienteById(a.cliente_id)?.razon_social?.toLowerCase() || '';
          bValue = getClienteById(b.cliente_id)?.razon_social?.toLowerCase() || '';
          break;
        case 'tipo':
          aValue = getTipoDocumentoInfo(a.tipo).toLowerCase();
          bValue = getTipoDocumentoInfo(b.tipo).toLowerCase();
          break;
        case 'subido':
          aValue = new Date(a.fecha_subida).getTime();
          bValue = new Date(b.fecha_subida).getTime();
          break;
        case 'estado':
          aValue = a.estado.toLowerCase();
          bValue = b.estado.toLowerCase();
          break;
        case 'asesor':
          aValue = user?.nombre?.toLowerCase() || '';
          bValue = user?.nombre?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getDocumentosDelCliente = (clienteId: string): DocumentoDB[] => {
    const filtered = documentos.filter((doc) => doc.cliente_id === clienteId);
    return getSortedDocumentos(filtered);
  };

  /* ── Sort arrow helper ── */
  const SortArrow = ({ col }: { col: string }) =>
    sortColumn === col ? <span className="ml-1 text-calm-text">{sortDirection === 'asc' ? '↑' : '↓'}</span> : null;

  /* ── Toggle form section ── */
  const toggleFormSection = (section: 'datos' | 'asiento' | 'notas') => {
    setFormSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  /* ── CSV Export ── */
  const handleExportCSV = () => {
    const allDocs = clienteSeleccionado ? getDocumentosDelCliente(clienteSeleccionado) : documentos;
    if (allDocs.length === 0) {
      toast.error('No hay documentos para exportar');
      return;
    }

    const headers = ['Documento', 'Cliente', 'NIF', 'Tipo', 'Estado', 'Nº Asiento', 'Fecha Subida'];
    const rows = allDocs.map(doc => {
      const cliente = getClienteById(doc.cliente_id);
      return [
        doc.nombre,
        cliente?.razon_social || '',
        cliente?.nif || '',
        getTipoDocumentoInfo(doc.tipo),
        getEstadoInfo(doc.estado).label,
        doc.nro_asiento_erp || '',
        new Date(doc.fecha_subida).toLocaleDateString('es-ES'),
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contabilizacion-manual-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Exportación CSV descargada');
  };

  /* ── Bulk contabilizar selected ── */
  const handleContabilizarSeleccionados = () => {
    if (seleccionados.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }
    const selectedPendientes = documentosPendientes.filter(d => seleccionados.has(d.id));
    if (selectedPendientes.length === 0) {
      toast.error('No hay documentos pendientes entre los seleccionados');
      return;
    }
    setDocumentosPendientes(selectedPendientes);
    setIndiceDocumentoActual(0);
    handleVerDocumento(selectedPendientes[0], 0);
  };

  /* ── Bulk mark as error ── */
  const handleMarcarComoError = async () => {
    if (seleccionados.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }
    if (!confirm(`¿Marcar ${seleccionados.size} documento(s) como error?`)) return;

    setBulkActionLoading(true);
    toast.loading('Marcando como error...');
    try {
      const ids = Array.from(seleccionados);
      let ok = 0;
      let errCount = 0;
      for (const docId of ids) {
        try {
          const res = await fetch('/api/documents/update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ documentoId: docId, updates: { estado: 'error' } }),
          });
          if (res.ok) ok++;
          else throw new Error();
        } catch {
          errCount++;
        }
      }
      toast.dismiss();
      if (errCount) toast.error(`Marcados: ${ok}. Fallos: ${errCount}`);
      else toast.success(`${ok} documento(s) marcado(s) como error`);
      setSeleccionados(new Set());
      await loadUnprocessedDocuments();
      if (clienteSeleccionado) {
        const docsCliente = getDocumentosDelCliente(clienteSeleccionado).filter(d => d.estado === 'pendiente');
        setDocumentosPendientes(docsCliente);
      }
    } catch {
      toast.dismiss();
      toast.error('Error al marcar documentos');
    } finally {
      setBulkActionLoading(false);
    }
  };

  /* ── Computed: documents for table with status filter ── */
  const documentosParaTabla = (() => {
    if (!clienteSeleccionado) return [];
    const todos = getDocumentosDelCliente(clienteSeleccionado);
    if (statusFilter === 'todos') return todos;
    return todos.filter(d => d.estado === statusFilter);
  })();

  /* ── Computed: status counts for pills ── */
  const statusCountsForPills = (() => {
    if (!clienteSeleccionado) return { todos: 0, pendiente: 0, contabilizado: 0, error: 0 };
    const todos = getDocumentosDelCliente(clienteSeleccionado);
    return {
      todos: todos.length,
      pendiente: todos.filter(d => d.estado === 'pendiente').length,
      contabilizado: todos.filter(d => d.estado === 'contabilizado').length,
      error: todos.filter(d => d.estado === 'error').length,
    };
  })();

  /* ── Loading state ── */
  if (loading || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-calm-bg">
        <div className="text-center space-y-4">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-4 w-48 mx-auto rounded-huge" />
        </div>
      </div>
    );
  }

  const clienteStats = getClienteStats();
  const totalPendientes = documentos.filter(d => d.estado === 'pendiente').length;
  const totalContabilizados = documentos.filter(d => d.estado === 'contabilizado').length;
  const totalErrores = documentos.filter(d => d.estado === 'error').length;
  const totalEnCola = documentos.filter(d => d.estado === 'en_proceso').length;
  const hoy = new Date().toISOString().slice(0, 10);
  const contabilizadosHoy = documentos.filter(
    d => d.estado === 'contabilizado' && d.fecha_subida.slice(0, 10) === hoy
  ).length;

  return (
    <>
      <Head>
        <title>Contabilización Manual - Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        {mostrarFormulario && documentoSeleccionado ? (
          /* ══════════════════════════════════════════════════════════════
             FULL-PAGE SPLIT VIEW — Inmatic-style document accounting
             ══════════════════════════════════════════════════════════════ */
          <div className="h-[calc(100vh-72px)] flex flex-col bg-calm-bg">
            {/* ── Top Navigation Bar ── */}
            <div className="bg-white border-b border-calm-border px-6 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleTerminar}
                  className="flex items-center gap-2 text-sm font-light text-calm-muted hover:text-calm-text transition-colors"
                >
                  <Icon name="arrow_back" className="text-[20px]" />
                  <span>Volver</span>
                </button>
                <div className="h-5 w-px bg-calm-border" />
                <div>
                  <h2 className="font-display font-light text-calm-text text-lg leading-tight">Documento a contabilizar</h2>
                  <p className="text-xs font-light text-calm-muted">
                    {getClienteById(documentoSeleccionado.cliente_id)?.razon_social || 'N/A'} · {getClienteById(documentoSeleccionado.cliente_id)?.nif || ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Document counter */}
                <div className="flex items-center gap-2 bg-calm-bg border border-calm-border rounded-huge px-4 py-2">
                  <button
                    onClick={() => {
                      if (indiceDocumentoActual > 0) {
                        const prev = documentosPendientes[indiceDocumentoActual - 1];
                        setIndiceDocumentoActual(indiceDocumentoActual - 1);
                        setDocumentoSeleccionado(prev);
                        if (prev.datos_ia) {
                          setFormularioAsiento({
                            tipoDocumento: prev.datos_ia.tipoDocumento || '',
                            asiento: prev.nro_asiento_erp || '',
                            notas: prev.datos_ia.notas || '',
                          });
                        }
                      }
                    }}
                    disabled={indiceDocumentoActual <= 0}
                    className="p-0.5 rounded-lg text-calm-muted hover:text-calm-text disabled:opacity-30 transition-colors"
                  >
                    <Icon name="navigate_before" className="text-[20px]" />
                  </button>
                  <span className="text-sm font-light text-calm-text tabular-nums min-w-[80px] text-center">
                    {indiceDocumentoActual + 1} / {documentosPendientes.length}
                  </span>
                  <button
                    onClick={() => {
                      if (indiceDocumentoActual < documentosPendientes.length - 1) {
                        handleSiguienteDocumento();
                      }
                    }}
                    disabled={indiceDocumentoActual >= documentosPendientes.length - 1}
                    className="p-0.5 rounded-lg text-calm-muted hover:text-calm-text disabled:opacity-30 transition-colors"
                  >
                    <Icon name="navigate_next" className="text-[20px]" />
                  </button>
                </div>

                {/* Quick actions */}
                <button
                  onClick={() => window.open(documentoSeleccionado.url_archivo, '_blank')}
                  className="p-2 rounded-xl text-calm-muted hover:text-calm-text hover:bg-calm-bg transition-all"
                  title="Abrir en nueva pestaña"
                >
                  <Icon name="open_in_new" className="text-[20px]" />
                </button>
                <button
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = documentoSeleccionado.url_archivo;
                    a.download = documentoSeleccionado.nombre;
                    a.click();
                  }}
                  className="p-2 rounded-xl text-calm-muted hover:text-calm-text hover:bg-calm-bg transition-all"
                  title="Descargar"
                >
                  <Icon name="download" className="text-[20px]" />
                </button>
              </div>
            </div>

            {/* ── Split Content ── */}
            <div className="flex-1 flex overflow-hidden">
              {/* ── LEFT: PDF Viewer (~60%) ── */}
              <div className="w-[60%] min-w-0">
                <PdfViewer
                  fileUrl={documentoSeleccionado.url_archivo}
                  fileName={documentoSeleccionado.nombre}
                />
              </div>

              {/* ── RIGHT: Accounting Form Panel (~40%) ── */}
              <div className="w-[40%] border-l border-calm-border bg-white flex flex-col overflow-hidden">
                {/* Form header */}
                <div className="px-6 py-4 border-b border-calm-border bg-calm-bg shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Registro Contable</p>
                      <p className="text-sm font-light text-calm-text mt-0.5 truncate max-w-[280px]" title={documentoSeleccionado.nombre}>
                        {documentoSeleccionado.nombre}
                      </p>
                    </div>
                    <Badge variant="outline" className={cn('rounded-full shrink-0', getEstadoInfo(documentoSeleccionado.estado).badge)}>
                      <span className={cn('w-2 h-2 rounded-full mr-1.5', getEstadoInfo(documentoSeleccionado.estado).dot)} />
                      {getEstadoInfo(documentoSeleccionado.estado).label}
                    </Badge>
                  </div>
                </div>

                {/* Scrollable form content */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                  {/* Progress indicator */}
                  <div className="bg-calm-bg border border-calm-border rounded-huge px-4 py-3 flex items-center justify-between">
                    <span className="text-sm font-light text-calm-text">
                      Documento {indiceDocumentoActual + 1} de {documentosPendientes.length}
                    </span>
                    <span className="text-xs font-light text-calm-muted">
                      {documentosPendientes.length - indiceDocumentoActual - 1} restante(s)
                    </span>
                  </div>

                  {/* ── Section: Datos del Documento ── */}
                  <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
                    <button
                      onClick={() => toggleFormSection('datos')}
                      className="flex items-center justify-between w-full px-5 py-3 bg-calm-bg hover:bg-calm-bg/70 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="description" className="text-[18px] text-calm-accent" />
                        <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Datos del Documento</span>
                      </div>
                      <Icon name={formSections.datos ? 'expand_more' : 'chevron_right'} className="text-[18px] text-calm-muted" />
                    </button>
                    {formSections.datos && (
                      <div className="p-5 space-y-4">
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">
                            Tipo documento <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={formularioAsiento.tipoDocumento || undefined}
                            onValueChange={(v) => setFormularioAsiento({ ...formularioAsiento, tipoDocumento: v })}
                          >
                            <SelectTrigger className="border-calm-border rounded-huge text-sm font-light">
                              <SelectValue placeholder="Seleccionar tipo..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="emitida_pdf">Facturas emitidas (PDF)</SelectItem>
                              <SelectItem value="recibida_pdf">Factura recibidas (PDF)</SelectItem>
                              <SelectItem value="emitida_excel">Facturas emitidas (Excel)</SelectItem>
                              <SelectItem value="recibida_excel">Facturas recibidas (Excel)</SelectItem>
                              <SelectItem value="otro">Otros</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="text-xs font-light text-calm-muted bg-calm-bg rounded-xl px-3 py-2">
                          <span className="font-medium text-calm-text">Cliente:</span>{' '}
                          {getClienteById(documentoSeleccionado.cliente_id)?.razon_social || 'N/A'}
                        </div>
                        {documentoSeleccionado.datos_ia && (
                          <div className="space-y-2">
                            {documentoSeleccionado.datos_ia.proveedor && (
                              <div className="text-xs font-light text-calm-muted bg-calm-bg rounded-xl px-3 py-2">
                                <span className="font-medium text-calm-text">Proveedor (IA):</span>{' '}
                                {documentoSeleccionado.datos_ia.proveedor}
                              </div>
                            )}
                            {documentoSeleccionado.datos_ia.fechaFactura && (
                              <div className="text-xs font-light text-calm-muted bg-calm-bg rounded-xl px-3 py-2">
                                <span className="font-medium text-calm-text">Fecha Factura (IA):</span>{' '}
                                {documentoSeleccionado.datos_ia.fechaFactura}
                              </div>
                            )}
                            {documentoSeleccionado.datos_ia.totalFactura != null && (
                              <div className="text-xs font-light text-calm-muted bg-calm-bg rounded-xl px-3 py-2">
                                <span className="font-medium text-calm-text">Total (IA):</span>{' '}
                                {Number(documentoSeleccionado.datos_ia.totalFactura).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Section: Asiento Contable ── */}
                  <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge overflow-hidden">
                    <button
                      onClick={() => toggleFormSection('asiento')}
                      className="flex items-center justify-between w-full px-5 py-3 bg-calm-bg hover:bg-calm-bg/70 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="tag" className="text-[18px] text-calm-accent" />
                        <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Asiento Contable</span>
                      </div>
                      <Icon name={formSections.asiento ? 'expand_more' : 'chevron_right'} className="text-[18px] text-calm-muted" />
                    </button>
                    {formSections.asiento && (
                      <div className="p-5 space-y-4">
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">
                            Nº Asiento <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            value={formularioAsiento.asiento}
                            onChange={(e) => setFormularioAsiento({ ...formularioAsiento, asiento: e.target.value })}
                            placeholder="Número de asiento"
                            className="border-calm-border rounded-huge text-sm font-light"
                          />
                        </div>
                        {formularioAsiento.asiento && (
                          <div className="flex items-center gap-2 text-xs font-light text-calm-muted bg-calm-bg rounded-xl px-3 py-2">
                            <Icon name="tag" className="text-[14px] text-calm-accent" />
                            <span>
                              Siguiente sugerido: <span className="font-medium text-calm-text">{calcularSiguienteNumeroAsiento()}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Section: Notas e Incidencias ── */}
                  <div className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge overflow-hidden">
                    <button
                      onClick={() => toggleFormSection('notas')}
                      className="flex items-center justify-between w-full px-5 py-3 bg-calm-bg hover:bg-calm-bg/70 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="comment" className="text-[18px] text-calm-accent" />
                        <span className="text-[10px] uppercase tracking-widest text-calm-accent font-medium">Notas e Incidencias</span>
                      </div>
                      <Icon name={formSections.notas ? 'expand_more' : 'chevron_right'} className="text-[18px] text-calm-muted" />
                    </button>
                    {formSections.notas && (
                      <div className="p-5 space-y-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">
                            Notas <span className="text-red-500">*</span> <span className="text-calm-accent font-normal normal-case">(para crear incidencia)</span>
                          </Label>
                          <Textarea
                            rows={3}
                            value={formularioAsiento.notas}
                            onChange={(e) => setFormularioAsiento({ ...formularioAsiento, notas: e.target.value })}
                            placeholder="Explica el motivo. El cliente recibirá esta nota."
                            className="resize-none border-calm-border rounded-huge text-sm font-light"
                          />
                        </div>
                        <p className="text-xs font-light text-calm-muted">
                          Si escribes notas y haces clic en &quot;Crear Incidencia&quot;, el documento se marcará como incidencia sin contabilizar.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Action Buttons (fixed at bottom) ── */}
                <div className="px-6 py-4 border-t border-calm-border bg-white shrink-0 space-y-3">
                  {/* Primary: Contabilizar */}
                  <button
                    onClick={handleGuardarAsiento}
                    className="w-full bg-calm-text text-white rounded-huge px-5 py-3 text-sm font-light hover:bg-calm-text/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-40"
                    disabled={!formularioAsiento.tipoDocumento || !formularioAsiento.asiento}
                  >
                    <Icon name="check_circle" className="text-[20px]" />
                    Contabilizar
                  </button>
                  <p className="text-xs font-light text-calm-muted text-center">
                    {formularioAsiento.notas && formularioAsiento.notas.trim() 
                      ? 'También creará una incidencia con las notas'
                      : 'Requiere tipo documento y nº asiento'}
                  </p>

                  {/* Secondary actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleCrearIncidencia}
                      className="border border-amber-200 rounded-huge px-4 py-2.5 text-sm font-light text-amber-600 hover:bg-amber-50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40"
                      disabled={!formularioAsiento.notas || !formularioAsiento.notas.trim()}
                    >
                      <Icon name="warning" className="text-[16px]" />
                      Incidencia
                    </button>
                    <button
                      onClick={handleEliminarDocumentoActual}
                      className="border border-red-200 rounded-huge px-4 py-2.5 text-sm font-light text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Icon name="delete" className="text-[16px]" />
                      Eliminar
                    </button>
                  </div>

                  {/* Navigation */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleTerminar}
                      className="border border-calm-border rounded-huge px-4 py-2.5 text-sm font-light text-calm-muted hover:bg-calm-bg hover:text-calm-text transition-all flex items-center justify-center gap-1.5"
                    >
                      <Icon name="close" className="text-[16px]" />
                      Terminar
                    </button>
                    <button
                      onClick={handleSiguienteDocumento}
                      className="bg-calm-bg border border-calm-border rounded-huge px-4 py-2.5 text-sm font-light text-calm-text hover:bg-calm-bg/70 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40"
                      disabled={indiceDocumentoActual >= documentosPendientes.length - 1}
                    >
                      {indiceDocumentoActual >= documentosPendientes.length - 1 
                        ? 'Último doc.' 
                        : formularioAsiento.asiento 
                          ? `Sig. ${calcularSiguienteNumeroAsiento()}`
                          : `Siguiente`}
                      <Icon name="navigate_next" className="text-[16px]" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ══════════════════════════════════════════════════════════════
             NORMAL PAGE — Client cards, document table, stats
             ══════════════════════════════════════════════════════════════ */
          <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 space-y-12 bg-calm-bg">

            {/* ── Page Header ── */}
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pt-12">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">Registro Manual</p>
                <h1 className="font-outfit text-5xl font-light text-calm-text tracking-tight">Contabilización Manual</h1>
                <p className="text-sm font-light text-calm-muted mt-2">Registro manual de facturas e integración ERP</p>
                
                {/* ── Info Pills: Manual vs IA ── */}
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  <div className="flex items-center gap-2 bg-fresh-sky-50 border border-fresh-sky-200/40 rounded-2xl px-4 py-2">
                    <span className="material-symbols-outlined text-[16px] text-fresh-sky-600">edit_note</span>
                    <span className="text-xs font-light text-fresh-sky-700">Manual — Revisas cada documento, asignas nº de asiento y contabilizas manualmente</span>
                  </div>
                  <a href="/asesor/contabilizacion-ia" className="flex items-center gap-2 bg-slate-50 border border-calm-border rounded-2xl px-4 py-2 hover:bg-white hover:shadow-sm transition-all">
                    <span className="material-symbols-outlined text-[16px] text-calm-accent">psychology</span>
                    <span className="text-xs font-light text-calm-muted hover:text-calm-text">Automática (IA) — La IA extrae datos, sugiere asientos y puedes aprobar en lote</span>
                    <span className="material-symbols-outlined text-[14px] text-calm-accent">arrow_forward</span>
                  </a>
                </div>
              </div>
            </div>

            {/* ── Stats Cards ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-huge bg-amber-50 flex items-center justify-center">
                    <Icon name="schedule" className="text-[20px] text-amber-600" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-calm-muted">Total Pendientes</p>
                    <p className="text-2xl font-light text-calm-text mt-1 tabular-nums">{totalPendientes}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-huge bg-emerald-50 flex items-center justify-center">
                    <Icon name="check_circle" className="text-[20px] text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-calm-muted">Contabilizados Hoy</p>
                    <p className="text-2xl font-light text-emerald-600 mt-1 tabular-nums">{contabilizadosHoy}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-white to-red-50/20 border border-red-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-huge bg-red-50 flex items-center justify-center">
                    <Icon name="cancel" className="text-[20px] text-red-600" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-calm-muted">Con Errores</p>
                    <p className="text-2xl font-light text-red-600 mt-1 tabular-nums">{totalErrores}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-white to-blue-50/20 border border-blue-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-huge bg-blue-50 flex items-center justify-center">
                    <Icon name="pending" className="text-[20px] text-blue-600" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-calm-muted">En Cola</p>
                    <p className="text-2xl font-light text-blue-600 mt-1 tabular-nums">{totalEnCola}</p>
                  </div>
                </div>
              </div>
            </div>

            {vistaActual === 'clientes' ? (
              /* ── Client Cards View ── */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {clienteStats.map((stats) => {
                  const iniciales = stats.cliente.razon_social
                    .split(' ')
                    .map((palabra) => palabra[0])
                    .join('')
                    .substring(0, 2)
                    .toUpperCase();

                  return (
                    <div
                      key={stats.cliente.id}
                      className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 hover:shadow-sm transition-all cursor-pointer group"
                      onClick={() => handleClienteClick(stats.cliente.id)}
                    >
                      <div className="flex items-start space-x-4 mb-5">
                        <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-calm-text font-display font-light text-base shrink-0">
                          {iniciales}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display font-light text-calm-text text-lg truncate">
                            {stats.cliente.razon_social}
                          </h3>
                          <p className="text-sm font-light text-calm-muted">{stats.cliente.nif}</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] uppercase tracking-widest text-calm-muted">Último documento</span>
                          <span className="text-sm font-light text-calm-text">
                            {stats.ultimoDocumento
                              ? `hace ${Math.floor(
                                  (Date.now() - stats.ultimoDocumento.getTime()) /
                                    (1000 * 60 * 60 * 24)
                                )} días`
                              : '-'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-5 pt-5 border-t border-calm-border flex justify-between items-center">
                        <div className="text-center flex-1">
                          <div className="text-2xl font-light text-calm-text tabular-nums">
                            {stats.pendientes}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-calm-muted mt-1">a contabilizar</div>
                        </div>
                        <div className="h-8 w-px bg-calm-border"></div>
                        <div className="text-center flex-1">
                          <div className="text-2xl font-light text-emerald-600 tabular-nums">
                            {stats.procesados}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-calm-muted mt-1">procesados</div>
                        </div>
                        {stats.errores > 0 && (
                          <>
                            <div className="h-8 w-px bg-calm-border"></div>
                            <div className="text-center flex-1">
                              <div className="text-2xl font-light text-red-500 tabular-nums">
                                {stats.errores}
                              </div>
                              <div className="text-[10px] uppercase tracking-widest text-calm-muted mt-1">errores</div>
                            </div>
                          </>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClienteClick(stats.cliente.id);
                        }}
                        className="w-full mt-6 bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors flex items-center justify-center gap-2"
                      >
                        <Icon name="work" className="text-[18px]" />
                        Trabajar
                      </button>
                    </div>
                  );
                })}

                {clienteStats.length === 0 && (
                  <div className="col-span-full bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-16 text-center">
                    <Icon name="group" className="text-[56px] text-calm-accent mb-4 block" />
                    <h3 className="font-display font-light text-calm-text text-xl mb-2">
                      No tienes clientes asignados
                    </h3>
                    <p className="text-sm font-light text-calm-muted">
                      No hay clientes asignados a tu cuenta.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* ── Documents View ── */
              <>
                {clienteSeleccionado && (
                  <>
                    {/* Client info header */}
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                      <div className="flex items-center justify-between border-b border-steel-azure-200/40 pb-5 mb-5">
                        <button
                          onClick={handleVolverAClientes}
                          className="border border-steel-azure-200/40 rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text hover:shadow-sm transition-all flex items-center gap-2"
                        >
                          <Icon name="arrow_back" className="text-[18px]" />
                          Volver a Clientes
                        </button>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={handleExportCSV}
                            className="border border-steel-azure-200/40 rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all flex items-center gap-2"
                          >
                            <Icon name="table_chart" className="text-[18px]" />
                            Exportar Excel
                          </button>
                          <span className="px-4 py-2 text-sm font-light text-calm-text border-b-2 border-calm-text">
                            Trabajar
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm font-light text-calm-text">
                          <span 
                            className="font-medium text-calm-text cursor-pointer hover:underline"
                            onClick={() => handleClienteClick(clienteSeleccionado)}
                            title="Click para refrescar la vista de este cliente"
                          >
                            ASESOR {user?.nombre?.toUpperCase() || 'NATALIO'}
                          </span> EMPRESA O AUTÓNOMO <span 
                            className="font-medium cursor-pointer hover:underline"
                            onClick={() => handleClienteClick(clienteSeleccionado)}
                            title="Click para refrescar la vista de este cliente"
                          >
                            {getClienteById(clienteSeleccionado)?.razon_social?.toUpperCase()}
                          </span> CON IDENTIFICACIÓN FISCAL <span 
                            className="font-medium cursor-pointer hover:underline"
                            onClick={() => handleClienteClick(clienteSeleccionado)}
                            title="Click para refrescar la vista de este cliente"
                          >
                            {getClienteById(clienteSeleccionado)?.nif}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* ── Status Filter Pills ── */}
                    <div className="flex flex-wrap gap-3">
                      {STATUS_PILLS.map(pill => {
                        const count = statusCountsForPills[pill.key as keyof typeof statusCountsForPills] ?? 0;
                        const isActive = statusFilter === pill.key;
                        return (
                          <button
                            key={pill.key}
                            onClick={() => setStatusFilter(pill.key)}
                            className={cn(
                              'rounded-huge px-5 py-2.5 text-sm font-light border transition-all flex items-center gap-2',
                              isActive
                                ? 'border-calm-text text-calm-text bg-white'
                                : 'bg-white border-calm-border text-calm-muted hover:border-calm-accent hover:text-calm-text'
                            )}
                          >
                            {pill.key !== 'todos' && (
                              <span
                                className={cn(
                                  'w-2 h-2 rounded-full',
                                  isActive ? 'bg-calm-text/40' : (ESTADO_INFO[pill.key]?.dot || 'bg-slate-300')
                                )}
                              />
                            )}
                            <span>{pill.label}</span>
                            <span
                              className={cn(
                                'text-xs tabular-nums',
                                isActive ? 'text-calm-text/60' : 'text-calm-accent'
                              )}
                            >
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* ── Bulk Actions Bar ── */}
                    {seleccionados.size > 0 && (
                      <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-6 flex flex-wrap items-center gap-3">
                        <span className="text-sm font-light text-calm-text">
                          {seleccionados.size} documento(s) seleccionado(s)
                        </span>
                        <button
                          onClick={handleContabilizarSeleccionados}
                          disabled={bulkActionLoading}
                          className="bg-calm-text text-white rounded-huge px-5 py-2 text-sm font-light hover:bg-calm-text/90 transition-colors flex items-center gap-2 disabled:opacity-40"
                        >
                          <Icon name="edit" className="text-[18px]" />
                          Contabilizar seleccionados
                        </button>
                        <button
                          onClick={handleMarcarComoError}
                          disabled={bulkActionLoading}
                          className="border border-red-200 rounded-huge px-5 py-2 text-sm font-light text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-40"
                        >
                          <Icon name="cancel" className="text-[18px]" />
                          Marcar como Error
                        </button>
                        <button
                          onClick={handleDescargarZip}
                          disabled={bulkActionLoading}
                          className="bg-calm-text text-white rounded-huge px-5 py-2 text-sm font-light hover:bg-calm-text/90 transition-colors flex items-center gap-2 disabled:opacity-40"
                        >
                          <Icon name="download" className="text-[18px]" />
                          Descargar ZIP
                        </button>
                        <button
                          onClick={() => setSeleccionados(new Set())}
                          disabled={bulkActionLoading}
                          className="border border-calm-border rounded-huge px-5 py-2 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-colors disabled:opacity-40"
                        >
                          Deseleccionar
                        </button>
                      </div>
                    )}

                    {/* ── Documents Table ── */}
                    <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
                      <div className="px-8 py-5 border-b border-fresh-sky-200/40 flex items-center justify-between">
                        <h3 className="text-[10px] uppercase tracking-widest text-calm-accent">
                          Documentos del Cliente
                          <span className="ml-2 text-sm font-light text-calm-muted normal-case">
                            ({documentosParaTabla.length} documento{documentosParaTabla.length !== 1 ? 's' : ''})
                          </span>
                        </h3>
                      </div>

                      {documentosParaTabla.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10 py-3 px-4">
                                <Checkbox
                                  checked={documentosParaTabla.length > 0 && documentosParaTabla.every(d => seleccionados.has(d.id))}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSeleccionados(new Set(documentosParaTabla.map(d => d.id)));
                                    } else {
                                      setSeleccionados(new Set());
                                    }
                                  }}
                                />
                              </TableHead>
                              <TableHead 
                                className="py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium cursor-pointer hover:text-calm-text select-none"
                                onClick={() => handleSort('documento')}
                              >
                                <div className="flex items-center">
                                  <span>Documento</span>
                                  <SortArrow col="documento" />
                                </div>
                              </TableHead>
                              <TableHead 
                                className="py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium cursor-pointer hover:text-calm-text select-none"
                                onClick={() => handleSort('empresa')}
                              >
                                <div className="flex items-center">
                                  <span>Empresa</span>
                                  <SortArrow col="empresa" />
                                </div>
                              </TableHead>
                              <TableHead 
                                className="py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium cursor-pointer hover:text-calm-text select-none"
                                onClick={() => handleSort('tipo')}
                              >
                                <div className="flex items-center">
                                  <span>Tipo</span>
                                  <SortArrow col="tipo" />
                                </div>
                              </TableHead>
                              <TableHead 
                                className="py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium cursor-pointer hover:text-calm-text select-none"
                                onClick={() => handleSort('subido')}
                              >
                                <div className="flex items-center">
                                  <span>Subido</span>
                                  <SortArrow col="subido" />
                                </div>
                              </TableHead>
                              <TableHead 
                                className="py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium cursor-pointer hover:text-calm-text select-none"
                                onClick={() => handleSort('estado')}
                              >
                                <div className="flex items-center">
                                  <span>Estado</span>
                                  <SortArrow col="estado" />
                                </div>
                              </TableHead>
                              <TableHead 
                                className="py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium cursor-pointer hover:text-calm-text select-none"
                                onClick={() => handleSort('asesor')}
                              >
                                <div className="flex items-center">
                                  <span>Asesor</span>
                                  <SortArrow col="asesor" />
                                </div>
                              </TableHead>
                              <TableHead className="text-right py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium">
                                Acciones
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {getSortedDocumentos(documentosParaTabla).map((doc, index) => {
                              const cliente = getClienteById(doc.cliente_id);
                              const estadoInfo = getEstadoInfo(doc.estado);
                              return (
                                <TableRow
                                  key={doc.id}
                                  className={cn(
                                    seleccionados.has(doc.id) && 'bg-calm-bg'
                                  )}
                                >
                                  <TableCell className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      checked={seleccionados.has(doc.id)}
                                      onCheckedChange={() => toggleSeleccion(doc.id)}
                                    />
                                  </TableCell>
                                  <TableCell className="py-3 px-4">
                                    <button
                                      className="text-calm-text hover:text-calm-accent text-sm font-light hover:underline"
                                      onClick={() => window.open(doc.url_archivo, '_blank')}
                                    >
                                      {doc.nombre}
                                    </button>
                                  </TableCell>
                                  <TableCell className="py-3 px-4 text-sm font-light text-calm-text">
                                    <span 
                                      className="cursor-pointer hover:text-calm-accent hover:underline"
                                      onClick={() => handleClienteClick(doc.cliente_id)}
                                      title="Click para trabajar con este cliente"
                                    >
                                      {cliente?.razon_social || 'N/A'}
                                    </span>
                                  </TableCell>
                                  <TableCell className="py-3 px-4 text-sm font-light text-calm-muted">
                                    {getTipoDocumentoInfo(doc.tipo)}
                                  </TableCell>
                                  <TableCell className="py-3 px-4 text-sm font-light text-calm-muted">
                                    {new Date(doc.fecha_subida).toLocaleString('es-ES', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </TableCell>
                                  {/* Status with Badge */}
                                  <TableCell className="py-3 px-4">
                                    <Badge variant="outline" className={cn('rounded-full', estadoInfo.badge)}>
                                      <span className={cn('w-2 h-2 rounded-full mr-1.5', estadoInfo.dot)} />
                                      {estadoInfo.label}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="py-3 px-4 text-sm font-light text-calm-text">
                                    <span 
                                      className="cursor-pointer hover:text-calm-accent hover:underline"
                                      onClick={() => handleClienteClick(doc.cliente_id)}
                                      title="Click para trabajar con este cliente"
                                    >
                                      {user?.nombre || 'NATALIO'}
                                    </span>
                                  </TableCell>
                                  <TableCell className="py-3 px-4">
                                    <div className="flex items-center justify-end space-x-1">
                                      <button
                                        onClick={() => window.open(doc.url_archivo, '_blank')}
                                        className="p-2 rounded-xl text-calm-muted hover:text-calm-text hover:bg-calm-bg transition-all"
                                        title="Ver documento"
                                      >
                                        <Icon name="visibility" className="text-[18px]" />
                                      </button>
                                      <button
                                        onClick={() => handleEliminarDocumento(doc.id)}
                                        className="p-2 rounded-xl text-calm-muted hover:text-red-600 hover:bg-red-50 transition-all"
                                        title="Eliminar"
                                      >
                                        <Icon name="delete" className="text-[18px]" />
                                      </button>
                                      {doc.estado === 'pendiente' && (
                                        <button
                                          onClick={() => handleContabilizarDocumento(doc, index)}
                                          className="p-2 rounded-xl text-calm-muted hover:text-calm-text hover:bg-calm-bg transition-all"
                                          title="Contabilizar"
                                        >
                                          <Icon name="edit" className="text-[18px]" />
                                        </button>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="text-center py-16">
                          <Icon
                            name={statusFilter === 'todos' ? 'check_circle' : 'search'}
                            className="text-[48px] text-calm-accent mb-4 block"
                          />
                          <h3 className="font-display font-light text-calm-text text-xl mb-2">
                            {statusFilter === 'todos'
                              ? 'No hay documentos para este cliente'
                              : `No hay documentos con estado "${getEstadoInfo(statusFilter).label}"`}
                          </h3>
                          <p className="text-sm font-light text-calm-muted">
                            {statusFilter === 'todos'
                              ? 'Todos los documentos de este cliente han sido procesados.'
                              : 'Prueba con otro filtro de estado.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </Layout>
    </>
  );
}
